import type { EditedPaths } from '@common/helpers';
import type { Uuid } from '@common/types';
import type { WebLegacyWidgetEntity } from '@web/types';
import type { WidgetSignalEntity } from './web-legacy-widget-signal-form.service';

import { Injectable } from '@angular/core';

import {
    changedPaths,
    defaultWidgetDataAdapter,
    isObject,
    mergeEditedPaths,
    stripUntouchedFills,
} from '@common/helpers';

import { backfillWidgetEntity } from './web-legacy-widget-signal-form.service';

type LoadedWidget = {
    /** The widget as the page stored it. */
    stored: WebLegacyWidgetEntity;
    /** What the editor fills in on top of it: the model defaults in `data`, then the signal form's own everywhere. */
    filled: WebLegacyWidgetEntity;
};

/**
 * Remembers each widget as the page stored it, so that a save can take back out what the editor only filled in
 * on load (#75695) — see {@link stripUntouchedFills}. A widget created in the editor has no entry and is saved whole.
 *
 * The whole widget, not just `data`: the signal form backfills `styles` too, and an image's filled
 * `borderTopLeftRadius: 0` overrides its stored `borderRadius` — the corners went square.
 */
@Injectable({
    providedIn: 'root',
})
export class WebWidgetLoadFillsService {
    private readonly loaded = new Map<Uuid, LoadedWidget>();
    private readonly edited = new Map<Uuid, EditedPaths>();

    public remember(widget: WebLegacyWidgetEntity, modelData: unknown): void {
        if (!isObject(widget?.data)) {
            return;
        }

        try {
            const stored = JSON.parse(JSON.stringify(widget)) as WebLegacyWidgetEntity;
            // The plain defaults, not the model's own `dataAdapter`: what that one adds (a moved key, a generated
            // id) is a migration to keep, and as it differs from the default it is never taken back out.
            const defaults = { ...stored, data: defaultWidgetDataAdapter(stored.data, modelData) };
            const filled = backfillWidgetEntity(
                defaults as unknown as WidgetSignalEntity
            ) as unknown as WebLegacyWidgetEntity;

            this.loaded.set(widget.id, { stored, filled });
            this.edited.delete(widget.id);
        } catch {
            // Not remembered, so saved whole — as before.
            this.loaded.delete(widget.id);
        }
    }

    public markEdited(id: Uuid, previous: unknown, next: unknown): void {
        const paths = mergeEditedPaths(this.edited.get(id), changedPaths(previous, next));
        if (paths) {
            this.edited.set(id, paths);
        }
    }

    /** Any part of the page tree, with the untouched fills of every remembered widget in it dropped. */
    public stripFills<T>(node: T): T {
        if (Array.isArray(node)) {
            return node.map((child) => this.stripFills(child)) as T;
        }

        if (!isObject(node)) {
            return node;
        }

        const entity = node as Record<string, any>;
        const loaded = typeof entity.name === 'string' ? this.loaded.get(entity.id) : undefined;

        if (loaded) {
            return stripUntouchedFills(entity, loaded.stored, loaded.filled, this.edited.get(entity.id));
        }

        return Object.fromEntries(Object.entries(entity).map(([key, value]) => [key, this.stripFills(value)])) as T;
    }
}
